export const formatPercent = (value: number): string =>
  new Intl.NumberFormat("default", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)

export const formatPercentDelta = (
  rateA: number | null,
  rateB: number | null,
): string | null => {
  if (rateA === null || rateB === null) {
    return null
  }
  const deltaPp = Math.round((rateA - rateB) * 100)
  if (deltaPp === 0) return "-"
  const sign = deltaPp > 0 ? "+" : ""
  return `${sign}${deltaPp}`
}

/** A response latency in milliseconds, as seconds with one decimal: "3.3 s". */
export const formatLatency = (latencyMs: number): string =>
  `${(latencyMs / 1000).toFixed(1)} s`

/** A duration in milliseconds, as minutes with one decimal: "2.5 min". */
export const formatDuration = (durationMs: number): string =>
  `${(durationMs / 60_000).toFixed(1)} min`

export const formatDate = (date: Date): string =>
  new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  }).format(date)

export const formatDateOnly = (date: Date): string =>
  new Intl.DateTimeFormat("en-US", {
    month: "numeric",
    day: "numeric",
  }).format(date)

export const formatDateLong = (date: Date): string =>
  new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date)

export const formatStatistic = (num = 0, maximumFractionDigits = 1): string =>
  num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  })

export const formatInteger = (num: number): string =>
  num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

export const formatTimeRange = (
  startDate: Date,
  endDate: Date,
  timeZone?: string,
): string => {
  const formatTime = (date: Date) =>
    date
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute:
          startDate.getMinutes() === 0 && endDate.getMinutes() === 0
            ? undefined
            : "2-digit",
        hour12: true,
        timeZone,
      })
      .toLowerCase()

  const range = `${formatTime(startDate)} - ${formatTime(endDate)}`
  const tz = new Intl.DateTimeFormat("en-US", {
    timeZoneName: "short",
    timeZone,
  })
    .formatToParts(startDate)
    .find((p) => p.type === "timeZoneName")!.value

  return `${range} ${tz}`
}

// For use with <input type="datetime-local" />, not human visible
// text.
export const formatForDatetimeLocal = (date: Date): string =>
  // Uses sv-SE locale because it uses the ISO-like format
  // "yyyy-MM-dd HH:mm" which is what we want, except with a space
  // instead of a "T". We replace the space with "T" below.
  new Intl.DateTimeFormat("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
    .format(date)
    .replace(" ", "T")
